import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  IconButton,
  Snackbar,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import NotificationAttachments from "../../NotificationAttachments";
import ChecklistAnswerHistoryDialog from "./ChecklistAnswerHistoryDialog";
import ChecklistReferencePhotos from "./ChecklistReferencePhotos";
import ChecklistEntryPhotos from "../../../MachineOverviewPage/_tabs/Maintenance/components/ChecklistEntryPhotos";
import {
  fetchChecklistReports,
  startCurrentReport
} from "../../../../services/checklistReportService";
import { toStoredPhoto } from "../../../../services/checklistPhotoService";
import {
  formatChecklistNumber,
  parseChecklistNumber
} from "../../../../utils/checklistNumber";
import {
  loadMaintenanceChecklistAnswersFromStorage,
  saveMaintenanceChecklistAnswersToStorage
} from "../../../../utils/maintenanceChecklistStorage";
import { saveChecklistItemAnswer } from "../../../../actions/checklists";
import { getNotification } from "../../../../actions/notification";
import {
  CHECKLIST_ATTACHMENTS_ENABLED,
  normalizeChecklistsFromMaintenanceTask,
  occurrenceStart,
  answerBelongsToOccurrence
} from "../../../../utils/checklistMapper";

const checklistTextFieldSx = {
  "& .MuiInputLabel-root": {
    backgroundColor: "#FFFFFF",
    px: "4px"
  }
};

const checklistInputLabelProps = {
  sx: {
    backgroundColor: "#FFFFFF",
    fontFamily: "Roboto, sans-serif",
    // No explicit fontSize here on purpose: MUI's outlined TextField already
    // shrinks the label from its default 16px to 12px (16 * 0.75) when
    // floated via a CSS transform. Forcing fontSize: 12 here made that same
    // 0.75 shrink apply a second time (rendering ~9px text), while the
    // border's notch gap stayed sized for the pre-shrink 12px — hence the
    // visible extra gap after the label once focused.
    letterSpacing: "0.4px",
    px: "4px"
  }
};

const getMaintenanceTask = notification =>
  notification?.notification?.hasmaintenancetask?.[0]?.maintenancetask ||
  notification?.hasmaintenancetask?.[0]?.maintenancetask;

const buildChecklistStorageIds = notification => {
  const maintenanceTask = getMaintenanceTask(notification);
  const workflowstepnodeid =
    notification?.notification?.hasworkflow?.[0]?.workflow?.hasworkflowstep?.[0]
      ?.workflowstep?.nodeid ||
    notification?.hasworkflow?.[0]?.workflow?.hasworkflowstep?.[0]?.workflowstep
      ?.nodeid;

  return {
    // The draft belongs to this Meldung, not to the task it comes from.
    notificationnodeid:
      notification?.notification?.nodeid || notification?.nodeid,
    maintenancetaskid: maintenanceTask?.maintenancetaskid,
    maintenancetasknodeid: maintenanceTask?.nodeid,
    workflowstepnodeid
  };
};

// Seeds from the item's `pastanswer` (the server's record of the last
// submitted report) when no local draft answer exists yet.
const createEntryAnswerDraft = (entry, currentAnswer = {}) => {
  const pastanswer = entry?.pastanswer || {};
  // A saved server answer wins over this browser's stored draft: every save
  // path sends the answers to the server, while the stored draft can be older
  // (e.g. the item was answered later or on another device). Photos are
  // local-only for now, so they still come from the draft.
  // An unanswered item's server values are only the backend's blank
  // placeholders (checkbox false, number 0, text "" — seeded for every item
  // when the first answer is saved), so they are never used as an answer.
  const serverAnswered = Boolean(pastanswer.isAnswered);

  return {
    checked: serverAnswered
      ? Boolean(pastanswer.checkboxvalue)
      : Boolean(currentAnswer.checked),
    numberValue:
      serverAnswered &&
      pastanswer.numbervalue !== undefined &&
      pastanswer.numbervalue !== null
        ? formatChecklistNumber(pastanswer.numbervalue)
        : currentAnswer.numberValue || "",
    textValue: serverAnswered
      ? pastanswer.textvalue || ""
      : currentAnswer.textValue || "",
    // Photos of the answer (report.items[].photos). Filled in from the item's
    // latest saved answer once the reports have loaded (see loadReportPhotos),
    // never from local storage: their links expire.
    photos: [],
    attachmentImages: Array.isArray(currentAnswer.attachmentImages)
      ? currentAnswer.attachmentImages
      : [],
    attachmentVideos: Array.isArray(currentAnswer.attachmentVideos)
      ? currentAnswer.attachmentVideos
      : [],
    attachmentDocs: Array.isArray(currentAnswer.attachmentDocs)
      ? currentAnswer.attachmentDocs
      : []
  };
};

const createChecklistAnswerDraft = (checklist, checklistAnswer = {}) => ({
  comment: checklistAnswer.comment || "",
  completed: Boolean(checklistAnswer.completed),
  entries: (checklist.entries || []).reduce((accumulator, entry) => {
    accumulator[entry.id] = createEntryAnswerDraft(
      entry,
      checklistAnswer.entries?.[entry.id]
    );
    return accumulator;
  }, {})
});

const isEntryAnswered = (entry, entryAnswer = {}) => {
  switch (entry.answerType) {
    case "checkbox":
      return Boolean(entryAnswer.checked);
    case "number":
      return parseChecklistNumber(entryAnswer.numberValue) !== null;
    case "text":
      return `${entryAnswer.textValue || ""}`.trim() !== "";
    default:
      return false;
  }
};

// Typed, but not a number ("1,2,3", "-") — shown as an error, never saved.
const isInvalidNumber = (entry, entryAnswer = {}) =>
  entry?.answerType === "number" &&
  `${entryAnswer.numberValue ?? ""}`.trim() !== "" &&
  parseChecklistNumber(entryAnswer.numberValue) === null;

// An entry's answer in a comparable form: its value (a number by value, so
// "7,6" and "7.6" match) and its photos. Equal to the saved answer means
// nothing to save — no "unsaved" hint, and no second, identical row in
// "Vergangene Antworten".
const answerFingerprint = (entry, entryAnswer = {}) => {
  const value =
    entry?.answerType === "checkbox"
      ? Boolean(entryAnswer.checked)
      : entry?.answerType === "number"
      ? parseChecklistNumber(entryAnswer.numberValue) ?? `${entryAnswer.numberValue ?? ""}`.trim()
      : `${entryAnswer.textValue ?? ""}`.trim();
  const photos = (entryAnswer.photos || []).map(photo => photo.fileid).sort();
  return JSON.stringify([value, photos]);
};

const hasEntryPhotos = (entryAnswer = {}) =>
  Array.isArray(entryAnswer?.photos) && entryAnswer.photos.length > 0;

// The backend refuses an answer without photos for every item with
// photouploadrequired — optional ones included (validateReportAgainstItems).
const needsPhotos = entry => CHECKLIST_ATTACHMENTS_ENABLED && Boolean(entry?.uploadRequired);

// The server's last-saved answer for this item, regardless of which client
// (this browser, another browser, the mobile app) actually submitted it —
// distinct from `entryAnswer`, this browser's own local draft/storage. Trusts
// the report item's own `isAnswered` flag rather than checking value
// presence: a checkbox's `checkboxvalue: false` is a real, valid value for
// "never actually answered" as much as for "explicitly left unchecked", and
// only the backend's own flag tells the two apart.
const isEntryAnsweredOnServer = entry => Boolean(entry?.pastanswer?.isAnswered);

// A closed checklist tile has no local `answers` for a checklist this
// browser never opened (e.g. completed via the mobile app instead) — without
// the pastanswer fallback it always read as freshly started even once the
// tab-level badge (driven by the same pastanswer data) already showed it as
// done. The open dialog's own draft is already seeded from pastanswer too
// (see createEntryAnswerDraft), so this is a no-op there, not a behavior change.
const getAnsweredCount = (checklist, answers) =>
  (checklist.entries || []).filter(entry =>
    isEntryAnswered(entry, answers?.entries?.[entry.id]) ||
    isEntryAnsweredOnServer(entry)
  ).length;

// The entry to auto-expand when a checklist dialog opens: the first one
// still unanswered (by either this browser's draft or the server's own
// pastanswer — same combined check getAnsweredCount uses), or "" when every
// entry is already answered, which collapses all of them (Accordion's
// `expanded={expandedEntryId === entry.id}` never matches a real id).
const findFirstUnansweredEntryId = (checklist, answers) => {
  const entry = (checklist.entries || []).find(
    entry =>
      !(
        isEntryAnswered(entry, answers?.entries?.[entry.id]) ||
        isEntryAnsweredOnServer(entry)
      )
  );
  return entry?.id || "";
};

const canCompleteChecklist = (checklist, answers) =>
  (checklist.entries || []).every(entry => {
    if (!entry.responseRequired) return true;
    return isEntryAnswered(entry, answers?.entries?.[entry.id]);
  });

const getMissingRequiredEntryIds = (checklist, answers) =>
  (checklist.entries || [])
    .filter(
      entry =>
        entry.responseRequired &&
        (!isEntryAnswered(entry, answers?.entries?.[entry.id]) ||
          // No way to upload while attachments are off, so a saved photo
          // requirement must not block completing the checklist.
          (needsPhotos(entry) && !hasEntryPhotos(answers?.entries?.[entry.id])))
    )
    .map(entry => entry.id);

const getOptionalSuffix = entry =>
  entry?.responseRequired ? "" : " (optional)";

const hasQuestionContent = entry =>
  Boolean(
    entry?.description ||
      (Array.isArray(entry?.referencePhotos) &&
        entry.referencePhotos.some(photo => photo?.url)) ||
      (Array.isArray(entry?.attachmentImages) && entry.attachmentImages.length) ||
      (Array.isArray(entry?.attachmentVideos) && entry.attachmentVideos.length) ||
      (Array.isArray(entry?.attachmentDocs) && entry.attachmentDocs.length)
  );

const serializeChecklistAnswerDraft = draft =>
  JSON.stringify({
    comment: draft?.comment || "",
    completed: Boolean(draft?.completed),
    entries: Object.keys(draft?.entries || {})
      .sort()
      .reduce((accumulator, entryId) => {
        const entry = draft.entries[entryId] || {};
        accumulator[entryId] = {
          checked: Boolean(entry.checked),
          numberValue: entry.numberValue || "",
          textValue: entry.textValue || "",
          photos: (entry.photos || []).map(photo => photo.fileid),
          attachmentImages: entry.attachmentImages || [],
          attachmentVideos: entry.attachmentVideos || [],
          attachmentDocs: entry.attachmentDocs || []
        };
        return accumulator;
      }, {})
  });

const HourglassIndicatorIcon = ({ color }) => (
  <svg
    width="14"
    height="17"
    viewBox="0 0 14 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.33333 15H10V12.5C10 11.5833 9.67361 10.7986 9.02083 10.1458C8.36806 9.49306 7.58333 9.16667 6.66667 9.16667C5.75 9.16667 4.96528 9.49306 4.3125 10.1458C3.65972 10.7986 3.33333 11.5833 3.33333 12.5V15ZM6.66667 7.5C7.58333 7.5 8.36806 7.17361 9.02083 6.52083C9.67361 5.86806 10 5.08333 10 4.16667V1.66667H3.33333V4.16667C3.33333 5.08333 3.65972 5.86806 4.3125 6.52083C4.96528 7.17361 5.75 7.5 6.66667 7.5ZM0.833333 16.6667C0.597222 16.6667 0.399306 16.5868 0.239583 16.4271C0.0798611 16.2674 0 16.0694 0 15.8333C0 15.5972 0.0798611 15.3993 0.239583 15.2396C0.399306 15.0799 0.597222 15 0.833333 15H1.66667V12.5C1.66667 11.6528 1.86458 10.8576 2.26042 10.1146C2.65625 9.37153 3.20833 8.77778 3.91667 8.33333C3.20833 7.88889 2.65625 7.29514 2.26042 6.55208C1.86458 5.80903 1.66667 5.01389 1.66667 4.16667V1.66667H0.833333C0.597222 1.66667 0.399306 1.58681 0.239583 1.42708C0.0798611 1.26736 0 1.06944 0 0.833333C0 0.597222 0.0798611 0.399306 0.239583 0.239583C0.399306 0.0798611 0.597222 0 0.833333 0H12.5C12.7361 0 12.934 0.0798611 13.0938 0.239583C13.2535 0.399306 13.3333 0.597222 13.3333 0.833333C13.3333 1.06944 13.2535 1.26736 13.0938 1.42708C12.934 1.58681 12.7361 1.66667 12.5 1.66667H11.6667V4.16667C11.6667 5.01389 11.4688 5.80903 11.0729 6.55208C10.6771 7.29514 10.125 7.88889 9.41667 8.33333C10.125 8.77778 10.6771 9.37153 11.0729 10.1146C11.4688 10.8576 11.6667 11.6528 11.6667 12.5V15H12.5C12.7361 15 12.934 15.0799 13.0938 15.2396C13.2535 15.3993 13.3333 15.5972 13.3333 15.8333C13.3333 16.0694 13.2535 16.2674 13.0938 16.4271C12.934 16.5868 12.7361 16.6667 12.5 16.6667H0.833333Z"
      fill={color}
      fillOpacity="1"
    />
  </svg>
);

const ErrorOutlineIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.16667 11.6667H10.8333V6.66667H9.16667V11.6667ZM10 15C10.2361 15 10.434 14.9201 10.5938 14.7604C10.7535 14.6007 10.8333 14.4028 10.8333 14.1667C10.8333 13.9306 10.7535 13.7326 10.5938 13.5729C10.434 13.4132 10.2361 13.3333 10 13.3333C9.76389 13.3333 9.56597 13.4132 9.40625 13.5729C9.24653 13.7326 9.16667 13.9306 9.16667 14.1667C9.16667 14.4028 9.24653 14.6007 9.40625 14.7604C9.56597 14.9201 9.76389 15 10 15ZM10 18.3333C8.84722 18.3333 7.76042 18.1146 6.73958 17.6771C5.71875 17.2396 4.82986 16.6458 4.07292 15.8958C3.32292 15.1389 2.72917 14.25 2.29167 13.2292C1.85417 12.2083 1.63542 11.1215 1.63542 9.96875C1.63542 8.81597 1.85417 7.72917 2.29167 6.70833C2.72917 5.6875 3.32292 4.80208 4.07292 4.05208C4.82986 3.29514 5.71875 2.70139 6.73958 2.27083C7.76042 1.83333 8.84722 1.61458 10 1.61458C11.1528 1.61458 12.2396 1.83333 13.2604 2.27083C14.2813 2.70139 15.1667 3.29514 15.9167 4.05208C16.6736 4.80208 17.2674 5.6875 17.6979 6.70833C18.1354 7.72917 18.3542 8.81597 18.3542 9.96875C18.3542 11.1215 18.1354 12.2083 17.6979 13.2292C17.2674 14.25 16.6736 15.1389 15.9167 15.8958C15.1667 16.6458 14.2813 17.2396 13.2604 17.6771C12.2396 18.1146 11.1528 18.3333 10 18.3333ZM10 16.6667C11.8472 16.6667 13.4201 16.0139 14.7188 14.7083C16.0243 13.4097 16.6771 11.8368 16.6771 9.98958C16.6771 8.14236 16.0243 6.56944 14.7188 5.27083C13.4201 3.96528 11.8472 3.3125 10 3.3125C8.15278 3.3125 6.57986 3.96528 5.28125 5.27083C3.97569 6.56944 3.32292 8.14236 3.32292 9.98958C3.32292 11.8368 3.97569 13.4097 5.28125 14.7083C6.57986 16.0139 8.15278 16.6667 10 16.6667Z"
      fill="#FF3D00"
    />
  </svg>
);

const getChecklistProgressMode = (answeredCount, entryCount) => {
  if (!entryCount || answeredCount <= 0) return "no-progress";
  if (answeredCount >= entryCount) return "full-progress";
  return "has-progress";
};

const ChecklistProgressIndicator = ({ answeredCount, entryCount }) => {
  const mode = getChecklistProgressMode(answeredCount, entryCount);
  const progress =
    entryCount > 0 ? Math.min(answeredCount / entryCount, 1) : 0;
  const size = 36;
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  const borderColor =
    mode === "full-progress"
      ? "#6FA03B"
      : "rgba(0, 0, 0, 0.12)";
  const iconColor =
    mode === "full-progress"
      ? "#6FA03B"
      : mode === "has-progress"
      ? "#6FA03B"
      : "rgba(0, 0, 0, 0.38)";

  return (
    <Box
      sx={{
        alignItems: "center",
        borderRadius: "50%",
        display: "flex",
        height: 36,
        justifyContent: "center",
        position: "relative",
        width: 36
      }}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${size} ${size}`}
        sx={{
          inset: 0,
          position: "absolute",
          transform: "rotate(-90deg)"
        }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={borderColor}
          strokeWidth={strokeWidth}
        />
        {mode === "has-progress" ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#6FA03B"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        ) : null}
      </Box>
      {mode === "full-progress" ? (
        <CheckRoundedIcon
          sx={{ color: iconColor, fontSize: 20, position: "relative", zIndex: 1 }}
        />
      ) : (
        <Box sx={{ lineHeight: 0, position: "relative", zIndex: 1 }}>
          <HourglassIndicatorIcon color={iconColor} />
        </Box>
      )}
    </Box>
  );
};

export default connect(
  ({ notification, checklistReportFailures, language }) => ({
    notification,
    checklistReportFailures,
    language
  }),
  {
    saveChecklistItemAnswer,
    getNotification,
    clearChecklistReportFailures: () => ({ type: "CLEAR_CHECKLIST_REPORT_FAILURES" })
  }
)(
  ({
    notification,
    onAnswersChange,
    // Beschreibung tab: the checklists can be opened and read (also
    // "Vergangene Antworten"), but nothing can be answered or saved; that
    // happens in the Durchführung tab (Mathias, 31.10.2026).
    readOnly = false,
    // Inside another tab's content: no page padding of its own.
    compact = false,
    checklistReportFailures,
    language,
    saveChecklistItemAnswer: sendItemAnswer,
    getNotification: refetchNotification,
    clearChecklistReportFailures
  }) => {
    const storageIds = useMemo(
      () => buildChecklistStorageIds(notification),
      [notification]
    );

    // Checklists (with full report history) already come embedded on the
    // maintenance task's `haschecklist` field — no separate fetch needed.
    const maintenanceTask = useMemo(
      () => getMaintenanceTask(notification),
      [notification]
    );
    const checklists = useMemo(
      () =>
        normalizeChecklistsFromMaintenanceTask(
          maintenanceTask,
          language,
          occurrenceStart(notification),
          notification?.notification?.nodeid
        ),
      [maintenanceTask, language, notification]
    );

    const [storedAnswers, setStoredAnswers] = useState(() =>
      loadMaintenanceChecklistAnswersFromStorage(storageIds)
    );
    // Going from one Meldung to another keeps this tab mounted: load the
    // other Meldung's draft instead of keeping the previous one's.
    const draftOwnerId = storageIds.notificationnodeid || "";
    const loadedDraftOwnerRef = useRef(draftOwnerId);
    useEffect(() => {
      if (loadedDraftOwnerRef.current === draftOwnerId) return;
      loadedDraftOwnerRef.current = draftOwnerId;
      setStoredAnswers(loadMaintenanceChecklistAnswersFromStorage(storageIds));
    }, [draftOwnerId]);
    const [selectedChecklist, setSelectedChecklist] = useState(null);
    const [historyEntry, setHistoryEntry] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [expandedEntryId, setExpandedEntryId] = useState("");
    const [invalidEntryIds, setInvalidEntryIds] = useState([]);
    const [showValidationError, setShowValidationError] = useState(false);
    const [initialDraftSnapshot, setInitialDraftSnapshot] = useState("");
    const [answerDraft, setAnswerDraft] = useState({
      comment: "",
      completed: false,
      entries: {}
    });
    // Entry ids whose current draft value has already been sent to the
    // backend via their own "Antwort speichern" — cleared per entry the
    // moment it's edited again (see updateEntryAnswer), so saveAllAnswers
    // only picks up entries that are genuinely new or changed since their
    // last individual save, instead of resending everything and duplicating
    // rows in that entry's history.
    const savedEntryIdsRef = useRef(new Set());

    // Entries the user has actually interacted with this session (set in
    // updateEntryAnswer, which only fires from a real input event) —
    // separate from the answer VALUE itself, because a checkbox's default
    // `checked: false` and a never-required text field's default `""` are
    // indistinguishable from "the user left it at its default on purpose"
    // without this. Gates hasSaveableValue below so saveAllAnswers() (the
    // "Später fortsetzen"/"Checkliste abschliessen" safety net) doesn't
    // fabricate real answers for entries nobody touched.
    const touchedEntryIdsRef = useRef(new Set());

    const openChecklistNodeIdRef = useRef("");
    const savedAnswersRef = useRef({}); // entryId -> answerFingerprint of its saved answer
    // Entries with a photo upload still running — nothing is saved meanwhile.
    const [uploadingEntryIds, setUploadingEntryIds] = useState([]);

    // @returns the reports, or null when the dialog moved on meanwhile
    const loadReports = async checklistNodeId => {
      const reports = await fetchChecklistReports(checklistNodeId);
      return openChecklistNodeIdRef.current === checklistNodeId ? reports : null;
    };

    // On opening: each photo entry's photos (with fresh links) for every one
    // the user hasn't touched yet — from the item's latest saved answer, the
    // same answer its value is prefilled from (pastanswer), also when that
    // answer's report is already complete.
    const loadReportPhotos = async checklist => {
      if (!CHECKLIST_ATTACHMENTS_ENABLED || !checklist?.nodeid) return;
      if (!(checklist.entries || []).some(needsPhotos)) return;
      try {
        const reports = await loadReports(checklist.nodeid);
        if (!reports) return;
        const latestById = new Map();
        reports.forEach(report =>
          (report?.items || []).forEach(item => {
            if (!item?.isAnswered) return;
            // Only this occurrence's answers (as the count and prefill).
            if (
              checklist.answersSince &&
              !answerBelongsToOccurrence(report, item, checklist.answersSince, checklist.notificationnodeid)
            ) return;
            const known = latestById.get(item.id);
            if (!known || (item.timestamp || 0) >= (known.timestamp || 0)) {
              latestById.set(item.id, item);
            }
          })
        );
        const photosById = new Map(
          [...latestById].map(([id, item]) => [id, Array.isArray(item.photos) ? item.photos : []])
        );
        if (!photosById.size) return;
        // Loaded photos are the starting point, not an edit: the snapshot the
        // "unsaved changes" check compares against takes them too.
        setInitialDraftSnapshot(snapshot => {
          if (!snapshot) return snapshot;
          const parsed = JSON.parse(snapshot);
          Object.keys(parsed.entries || {}).forEach(entryId => {
            if (!touchedEntryIdsRef.current.has(entryId) && photosById.has(entryId)) {
              parsed.entries[entryId].photos = photosById.get(entryId).map(photo => photo.fileid);
            }
          });
          return JSON.stringify(parsed);
        });
        setAnswerDraft(draft => ({
          ...draft,
          entries: Object.keys(draft.entries || {}).reduce((entries, entryId) => {
            entries[entryId] =
              touchedEntryIdsRef.current.has(entryId) || !photosById.has(entryId)
                ? draft.entries[entryId]
                : { ...draft.entries[entryId], photos: photosById.get(entryId) };
            // The loaded photos are part of the saved answer.
            const entry = (checklist.entries || []).find(candidate => candidate.id === entryId);
            if (entry && savedAnswersRef.current[entryId] !== undefined && entries[entryId] !== draft.entries[entryId]) {
              savedAnswersRef.current[entryId] = answerFingerprint(entry, entries[entryId]);
            }
            return entries;
          }, {})
        }));
      } catch (error) {
        console.error("Checklist reports could not be loaded:", error?.message || error);
      }
    };

    // Photos go under the current in-progress report, asked right before each
    // upload: it is started blank when there is none yet (a photo item
    // answered first), and a report completed meanwhile is never used.
    const resolveCurrentReportNodeId = async () =>
      selectedChecklist?.nodeid
        ? startCurrentReport(selectedChecklist.nodeid, notification?.notification?.nodeid)
        : "";

    const setEntryUploading = (entryId, uploading) =>
      setUploadingEntryIds(current =>
        uploading
          ? current.includes(entryId) ? current : [...current, entryId]
          : current.filter(id => id !== entryId)
      );

    const openChecklistDialog = checklist => {
      const checklistAnswer = storedAnswers?.[checklist.nodeid] || {};
      const nextDraft = createChecklistAnswerDraft(checklist, checklistAnswer);

      // The saved answer of every entry the server has one for (the draft is
      // prefilled from it); updated after each save.
      savedAnswersRef.current = (checklist.entries || []).reduce((saved, entry) => {
        if (entry.pastanswer?.isAnswered) {
          saved[entry.id] = answerFingerprint(entry, nextDraft.entries?.[entry.id]);
        }
        return saved;
      }, {});

      savedEntryIdsRef.current = new Set();
      touchedEntryIdsRef.current = new Set();
      setSelectedChecklist(checklist);
      setAnswerDraft(nextDraft);
      setInitialDraftSnapshot(serializeChecklistAnswerDraft(nextDraft));
      setExpandedEntryId(findFirstUnansweredEntryId(checklist, nextDraft));
      setDialogOpen(true);
      openChecklistNodeIdRef.current = checklist.nodeid || "";
      setUploadingEntryIds([]);
      loadReportPhotos(checklist);
    };

    const resetChecklistDialog = () => {
      setUnsavedHintEntryId("");
      openChecklistNodeIdRef.current = "";
      setUploadingEntryIds([]);
      setDialogOpen(false);
      setConfirmCloseOpen(false);
      setSelectedChecklist(null);
      setExpandedEntryId("");
      setInvalidEntryIds([]);
      setShowValidationError(false);
      setInitialDraftSnapshot("");
      setAnswerDraft({
        comment: "",
        completed: false,
        entries: {}
      });
    };

    const hasUnsavedChanges =
      dialogOpen &&
      initialDraftSnapshot &&
      serializeChecklistAnswerDraft(answerDraft) !== initialDraftSnapshot;

    const requestCloseChecklistDialog = () => {
      if (hasUnsavedChanges) {
        setConfirmCloseOpen(true);
        return;
      }

      resetChecklistDialog();
    };

    const handleDialogClose = (_event, reason) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        requestCloseChecklistDialog();
        return;
      }

      requestCloseChecklistDialog();
    };

    // An answer given in this session but not yet sent with "Antwort
    // speichern" (Mathias, 30.10.2026: typed values got lost when the user
    // moved on without saving). A checkbox the user touched is an answer
    // either way; a number or text only when something is typed; changed
    // photos count too. Opening an entry, or clearing a field back to
    // empty, is no answer.
    const [unsavedHintEntryId, setUnsavedHintEntryId] = useState("");

    const isUnchangedAnswer = (entry, entryAnswer) =>
      savedAnswersRef.current[entry.id] !== undefined &&
      savedAnswersRef.current[entry.id] === answerFingerprint(entry, entryAnswer);

    const markAnswerSaved = (entry, entryAnswer) => {
      savedEntryIdsRef.current.add(entry.id);
      savedAnswersRef.current[entry.id] = answerFingerprint(entry, entryAnswer);
    };

    const hasUnsavedAnswer = entryId => {
      if (!touchedEntryIdsRef.current.has(entryId) || savedEntryIdsRef.current.has(entryId)) {
        return false;
      }
      const entry = (selectedChecklist?.entries || []).find(candidate => candidate.id === entryId);
      const entryAnswer = answerDraft.entries?.[entryId] || {};
      if (!entry) return false;
      if (isUnchangedAnswer(entry, entryAnswer)) return false;
      if (hasEntryPhotos(entryAnswer)) return true;
      if (entry.answerType === "checkbox") return true;
      if (entry.answerType === "number") return `${entryAnswer.numberValue ?? ""}`.trim() !== "";
      return `${entryAnswer.textValue ?? ""}`.trim() !== "";
    };

    // Moving to another entry (or collapsing this one) waits until an
    // unsaved answer is saved; the entry says so instead.
    const handleEntryToggle = (entryId, isExpanded) => {
      const target = isExpanded ? entryId : "";
      if (expandedEntryId && expandedEntryId !== target && hasUnsavedAnswer(expandedEntryId)) {
        setUnsavedHintEntryId(expandedEntryId);
        return;
      }
      setUnsavedHintEntryId("");
      setExpandedEntryId(target);
    };

    const updateEntryAnswer = (entryId, patch) => {
      // The backend no longer has this value — an edit after "Antwort
      // speichern" must make saveAllAnswers pick this entry up again.
      savedEntryIdsRef.current.delete(entryId);
      touchedEntryIdsRef.current.add(entryId);
      setAnswerDraft(current => ({
        ...current,
        entries: {
          ...current.entries,
          [entryId]: {
            ...current.entries?.[entryId],
            ...patch
          }
        }
      }));
    };

    // Functional, because several uploads can finish at once and each adds
    // its photo to whatever the answer holds by then. A photo change is an
    // edit like any other: the answer must be saved again.
    const updateEntryPhotos = (entryId, update) => {
      savedEntryIdsRef.current.delete(entryId);
      touchedEntryIdsRef.current.add(entryId);
      setInvalidEntryIds(current => current.filter(id => id !== entryId));
      setAnswerDraft(current => ({
        ...current,
        entries: {
          ...current.entries,
          [entryId]: {
            ...current.entries?.[entryId],
            photos: update(current.entries?.[entryId]?.photos || [])
          }
        }
      }));
    };

    // Each answer is persisted via PUT /checklists/:nodeid/reports/current —
    // explicitly, on the item's own "Antwort speichern" click (handleSaveEntryAnswer),
    // not automatically as the user types/toggles.
    const buildReportItemValue = (entry, valuePatch) => {
      const reportItem = { id: entry.id };
      if ("checked" in valuePatch) {
        reportItem.checkboxvalue = Boolean(valuePatch.checked);
      } else if ("numberValue" in valuePatch) {
        // Only an empty optional field gets here without a number (an
        // invalid one is never sent, see hasSaveableValue) — it stays 0.
        reportItem.numbervalue = parseChecklistNumber(valuePatch.numberValue) ?? 0;
      } else if ("textValue" in valuePatch) {
        reportItem.textvalue = valuePatch.textValue || "";
      }
      // `photos` replaces the item's photo list, so it always carries all of
      // them — only the stored fields, never the read url or a preview.
      if (needsPhotos(entry)) {
        reportItem.photos = (valuePatch.photos || []).map(toStoredPhoto);
      }
      return reportItem;
    };

    // Skip saving an entry nobody touched this session — a checkbox always
    // HAS a value (checked or not) so it can't be judged by content the way
    // number/text can, and even those default to "" either way, so without
    // this gate `saveAllAnswers()` would send every optional/checkbox entry
    // as answered, including ones the user never looked at (see the bug this
    // fixed: an unopened, untouched checkbox entry was saved with
    // isAnswered:true, checked:false — indistinguishable afterwards from a
    // deliberate "no").
    const hasSaveableValue = (entry, valuePatch) => {
      if (!touchedEntryIdsRef.current.has(entry.id)) return false;
      // The backend would answer 400 "Answer is incomplete." without one.
      if (needsPhotos(entry) && !(valuePatch.photos || []).length) return false;
      if ("checked" in valuePatch) return true;
      if ("numberValue" in valuePatch) {
        const typed = `${valuePatch.numberValue ?? ""}`.trim();
        // Something typed that isn't a number is never sent (it was saved as 0).
        if (typed && parseChecklistNumber(typed) === null) return false;
        return !entry.responseRequired || typed !== "";
      }
      if ("textValue" in valuePatch) {
        return (
          !entry.responseRequired || Boolean((valuePatch.textValue || "").trim())
        );
      }
      return false;
    };

    const valuePatchForEntry = (entry, entryAnswer) => ({
      ...(entry.answerType === "checkbox"
        ? { checked: entryAnswer.checked }
        : entry.answerType === "number"
        ? { numberValue: entryAnswer.numberValue }
        : { textValue: entryAnswer.textValue }),
      photos: entryAnswer.photos || []
    });

    // @returns whether it actually sent anything (false for a blank,
    // not-yet-answered field — see hasSaveableValue) — callers use this to
    // decide whether the entry is truly saved, not just attempted.
    const saveEntryAnswerNow = (entry, valuePatch) => {
      if (!selectedChecklist || !hasSaveableValue(entry, valuePatch)) return false;
      sendItemAnswer(
        selectedChecklist.nodeid,
        {
          ...buildReportItemValue(entry, valuePatch),
          // Saved into this Meldung's own report, so two open Meldungen of
          // the same task don't share answers.
          ...(notification?.notification?.nodeid
            ? { notificationnodeid: notification.notification.nodeid }
            : {})
        },
        "SAVE_CHECKLIST_ITEM_ANSWER"
      );
      return true;
    };

    // "Antwort speichern" click: saves just this item, marks it as already
    // saved (so the bulk save below won't resend it), then collapses it and
    // expands the next one so the user can move straight on to it.
    const handleSaveEntryAnswer = entry => {
      if (uploadingEntryIds.includes(entry.id)) return;
      const entryAnswer = answerDraft.entries?.[entry.id] || {};
      if (isUnchangedAnswer(entry, entryAnswer)) {
        // The saved answer again: nothing is sent (no duplicate history row).
        savedEntryIdsRef.current.add(entry.id);
        setUnsavedHintEntryId("");
        const currentIndex = (selectedChecklist?.entries || []).findIndex(
          candidate => candidate.id === entry.id
        );
        const nextEntry = selectedChecklist?.entries?.[currentIndex + 1];
        setExpandedEntryId(nextEntry ? nextEntry.id : "");
        return;
      }
      if (isInvalidNumber(entry, entryAnswer)) {
        // Stays open, the field says what's wrong.
        setInvalidEntryIds(current =>
          current.includes(entry.id) ? current : [...current, entry.id]
        );
        return;
      }
      if (needsPhotos(entry) && !hasEntryPhotos(entryAnswer)) {
        // Stays open and red until a photo is added — not sent, not skipped.
        setInvalidEntryIds(current =>
          current.includes(entry.id) ? current : [...current, entry.id]
        );
        setShowValidationError(true);
        return;
      }
      if (saveEntryAnswerNow(entry, valuePatchForEntry(entry, entryAnswer))) {
        markAnswerSaved(entry, entryAnswer);
      }
      setUnsavedHintEntryId("");

      if (selectedChecklist) {
        const currentIndex = selectedChecklist.entries.findIndex(
          candidate => candidate.id === entry.id
        );
        const nextEntry = selectedChecklist.entries[currentIndex + 1];
        setExpandedEntryId(nextEntry ? nextEntry.id : "");
      }
    };

    // Safety net for "Checkliste abschliessen"/"Später fortsetzen": saves
    // only entries whose own "Antwort speichern" was never clicked (or was
    // clicked, then edited again — see updateEntryAnswer), so nothing typed
    // gets lost. Entries already saved with their current value are skipped,
    // since resending them just duplicates that entry's history with an
    // identical row.
    const saveAllAnswers = () => {
      if (!selectedChecklist) return;
      selectedChecklist.entries.forEach(entry => {
        if (savedEntryIdsRef.current.has(entry.id)) return;
        const entryAnswer = answerDraft.entries?.[entry.id] || {};
        // Changed and changed back: still the saved answer, not sent again.
        if (isUnchangedAnswer(entry, entryAnswer)) return;
        if (saveEntryAnswerNow(entry, valuePatchForEntry(entry, entryAnswer))) {
          markAnswerSaved(entry, entryAnswer);
        }
      });
    };

    const persistAnswers = completed => {
      if (!selectedChecklist || uploadingEntryIds.length) return;

      if (completed) {
        const missingEntryIds = getMissingRequiredEntryIds(
          selectedChecklist,
          answerDraft
        );

        if (missingEntryIds.length) {
          setInvalidEntryIds(missingEntryIds);
          setExpandedEntryId(missingEntryIds[0]);
          setShowValidationError(true);
          return;
        }
      }

      saveAllAnswers();

      const nextAnswers = {
        ...storedAnswers,
        [selectedChecklist.nodeid]: {
          comment: answerDraft.comment,
          completed,
          // Photos live on the report only — their links expire.
          entries: Object.keys(answerDraft.entries || {}).reduce((entries, entryId) => {
            const { photos, ...rest } = answerDraft.entries[entryId] || {};
            entries[entryId] = rest;
            return entries;
          }, {})
        }
      };

      // Photo answers aren't submitted to the backend yet (deferred until
      // the report photo-upload contract is ready) — keep them, and an
      // in-progress draft, in localStorage as before. Everything else was
      // already persisted incrementally above as the user filled it in.
      saveMaintenanceChecklistAnswersToStorage(storageIds, nextAnswers);
      setStoredAnswers(nextAnswers);
      setInvalidEntryIds([]);
      setShowValidationError(false);

      const notificationNodeId = notification?.notification?.nodeid;
      if (notificationNodeId) {
        // Refresh so the embedded haschecklist/haschecklistreport data
        // (tile progress, pastanswer prefill) reflects the new answers.
        refetchNotification(
          { nodeid: notificationNodeId },
          "GETSINGLENOTIFICATION"
        );
      }

      onAnswersChange?.(nextAnswers);
      resetChecklistDialog();
    };

    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: compact ? 1.5 : 2.5,
            // The Beschreibung tab styles every <p> with margin-bottom: 1em,
            // which stretched the tiles; "&&" outranks that rule.
            "&& p": { marginBottom: 0 },
            px: compact ? 0 : "24px",
            pt: compact ? 0 : "24px",
            pb: compact ? 0 : "24px"
          }}
        >
          <Typography
            sx={{
              color: "#0E3F51",
              fontFamily: "Bai Jamjuree, sans-serif",
              fontSize: compact ? 18 : 20,
              fontWeight: 600,
              letterSpacing: "0.15px",
              lineHeight: 1.4
            }}
          >
            Checklisten
          </Typography>

          {checklists.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {checklists.map(checklist => {
                const entryCount = Array.isArray(checklist.entries)
                  ? checklist.entries.length
                  : 0;
                const answeredCount = getAnsweredCount(
                  checklist,
                  storedAnswers?.[checklist.nodeid]
                );

                return (
                  <Box
                    key={checklist.nodeid || checklist._localKey}
                    data-testid="li_notification_checklist"
                    onClick={() => openChecklistDialog(checklist)}
                    sx={{
                      alignItems: "center",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      px: "20px",
                      py: "16px"
                    }}
                  >
                    <Box
                      sx={{ alignItems: "center", display: "flex", flex: 1, gap: 2 }}
                    >
                      <ChecklistProgressIndicator
                        answeredCount={answeredCount}
                        entryCount={entryCount}
                      />

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "#38748D",
                            fontFamily: "Bai Jamjuree, sans-serif",
                            fontSize: 16,
                            fontWeight: 600,
                            letterSpacing: "0.15px",
                            lineHeight: 1.4,
                            textTransform: "uppercase"
                          }}
                        >
                          {checklist.title || "Name der Checkliste"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(0, 0, 0, 0.5)",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 12,
                            fontWeight: 400,
                            letterSpacing: "0.4px",
                            lineHeight: 1.4
                          }}
                        >
                          {`${answeredCount}/${entryCount} beantwortet`}
                        </Typography>
                      </Box>
                    </Box>

                    <ChevronRightRoundedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 24 }}
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.5)",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 400,
                letterSpacing: "0.25px",
                lineHeight: 1.6
              }}
            >
              Keine Checklisten vorhanden.
            </Typography>
          )}
        </Box>

        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: "6px",
              boxShadow:
                "0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2)",
              m: 2,
              p: "28px 24px"
            }
          }}
        >
          {selectedChecklist ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ alignItems: "center", display: "flex", gap: 3 }}>
                <ChecklistProgressIndicator
                  answeredCount={getAnsweredCount(selectedChecklist, answerDraft)}
                  entryCount={selectedChecklist.entries.length}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#0E3F51",
                      fontFamily: "Bai Jamjuree, sans-serif",
                      fontSize: 20,
                      fontWeight: 600,
                      letterSpacing: "0.15px",
                      lineHeight: 1.4
                    }}
                  >
                    {selectedChecklist.title || "Name der Checkliste"}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(0, 0, 0, 0.5)",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 12,
                      fontWeight: 400,
                      letterSpacing: "0.4px",
                      lineHeight: 1.4
                    }}
                  >
                    {`${getAnsweredCount(
                      selectedChecklist,
                      answerDraft
                    )}/${selectedChecklist.entries.length} beantwortet`}
                  </Typography>
                </Box>

                <IconButton
                  onClick={requestCloseChecklistDialog}
                  sx={{ color: "rgba(0,0,0,0.38)" }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {selectedChecklist.entries.map((entry, entryIndex) => {
                  const entryAnswer = answerDraft.entries?.[entry.id] || {};
                  const isInvalid = invalidEntryIds.includes(entry.id);
                  const isMissingUpload =
                    needsPhotos(entry) &&
                    (entry.responseRequired || isInvalid) &&
                    !hasEntryPhotos(entryAnswer);
                  const showQuestionDivider = hasQuestionContent(entry);

                  return (
                    <Accordion
                      key={entry.id}
                      expanded={expandedEntryId === entry.id}
                      onChange={(_, isExpanded) =>
                        handleEntryToggle(entry.id, isExpanded)
                      }
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: isInvalid
                          ? "#FF3D00"
                          : "rgba(0, 0, 0, 0.12)",
                        borderRadius: "4px !important",
                        boxShadow: "none",
                        "&:before": { display: "none" }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreRoundedIcon
                            sx={{ color: "rgba(0,0,0,0.54)" }}
                          />
                        }
                        sx={{
                          minHeight: "unset",
                          px: 2,
                          py: "20px",
                          "& .MuiAccordionSummary-content": {
                            alignItems: "center",
                            gap: 1,
                            my: 0
                          }
                        }}
                      >
                        <Checkbox
                          // Same rule as the "x/y beantwortet" count: also a
                          // saved answer that is an unticked box or only a photo.
                          checked={
                            isEntryAnswered(entry, entryAnswer) ||
                            isEntryAnsweredOnServer(entry)
                          }
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            color: isInvalid ? "#FF3D00" : "rgba(0,0,0,0.54)",
                            p: 0,
                            pointerEvents: "none"
                          }}
                        />
                        <Typography
                          sx={{
                            color: isInvalid ? "#FF3D00" : "#0E3F51",
                            fontFamily: "Bai Jamjuree, sans-serif",
                            fontSize: 16,
                            fontWeight: 600,
                            letterSpacing: "0.15px",
                            lineHeight: 1.4
                          }}
                        >
                          {entry.title || "Title of the checklist entry"}
                        </Typography>
                        {!entry.responseRequired ? (
                          <Chip
                            label="Optional"
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: 12,
                              color: "rgba(0,0,0,0.6)",
                              borderColor: "rgba(0,0,0,0.23)"
                            }}
                          />
                        ) : null}
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          borderTop: "1px solid",
                          borderColor: isInvalid
                            ? "rgba(255, 61, 0, 0.24)"
                            : "rgba(0, 0, 0, 0.12)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          px: 2,
                          pb: 2.5,
                          pt: 2
                        }}
                      >
                        {entry.description ? (
                          <Typography
                            sx={{
                              color: "rgba(0, 0, 0, 0.7)",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: 14,
                              fontWeight: 400,
                              letterSpacing: "0.25px",
                              lineHeight: 1.6,
                              whiteSpace: "pre-wrap"
                            }}
                          >
                            {entry.description}
                          </Typography>
                        ) : null}

                        <ChecklistReferencePhotos photos={entry.referencePhotos} />

                        {hasQuestionContent(entry) &&
                        (entry.attachmentImages?.length ||
                          entry.attachmentVideos?.length ||
                          entry.attachmentDocs?.length) ? (
                          <NotificationAttachments
                            key={`checklist-question-${selectedChecklist.nodeid}-${entry.id}`}
                            from="maintenance_task_checklist_question"
                            edit={false}
                            attachmentsOf="maintenanceTask"
                            specifier="MaintenanceTaskChecklistQuestionAttachment"
                            workflowAttachments={[]}
                            localDraftAttachments={{
                              images: entry.attachmentImages || [],
                              videos: entry.attachmentVideos || [],
                              docs: entry.attachmentDocs || []
                            }}
                          />
                        ) : null}

                        {showQuestionDivider ? (
                          <Box sx={{ pt: 1 }}>
                            <Box
                              sx={{
                                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                                width: "100%"
                              }}
                            />
                            <Box sx={{ height: 20 }} />
                            <Typography
                              sx={{
                                width: "100%",
                                position: "relative",
                                fontSize: 16,
                                letterSpacing: "0.15px",
                                lineHeight: "140%",
                                fontWeight: 600,
                                fontFamily: "Bai Jamjuree, sans-serif",
                                color: "rgba(0, 0, 0, 0.87)",
                                textAlign: "left",
                                display: "inline-block"
                              }}
                            >
                              Ihre Eingaben
                            </Typography>
                          </Box>
                        ) : null}

                        {entry.answerType === "checkbox" ? (
                          <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                            <Checkbox
                              checked={Boolean(entryAnswer.checked)}
                              disabled={readOnly}
                              onChange={event =>
                                updateEntryAnswer(entry.id, {
                                  checked: event.target.checked
                                })
                              }
                              inputProps={{
                                "data-testid": "chkbx_checklist_answer-" + entryIndex
                              }}
                              sx={{ p: 0 }}
                            />
                            <Typography
                              sx={{
                                color: "rgba(0,0,0,0.87)",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 16,
                                fontWeight: 400,
                                letterSpacing: "0.5px",
                                lineHeight: 1.4
                              }}
                            >
                              {(entry.answerTitle || "Antworttext") +
                                getOptionalSuffix(entry)}
                            </Typography>
                          </Box>
                        ) : null}

                        {entry.answerType === "number" ? (
                          <TextField
                            fullWidth
                            sx={checklistTextFieldSx}
                            label={
                              (entry.answerTitle || "Antworttext") +
                              getOptionalSuffix(entry)
                            }
                            value={entryAnswer.numberValue || ""}
                            disabled={readOnly}
                            error={isInvalidNumber(entry, entryAnswer)}
                            helperText={
                              isInvalidNumber(entry, entryAnswer)
                                ? "Bitte eine gültige Zahl eingeben, z. B. 12,5"
                                : undefined
                            }
                            onChange={event =>
                              updateEntryAnswer(entry.id, {
                                numberValue: event.target.value.replace(
                                  /[^0-9.,-]/g,
                                  ""
                                )
                              })
                            }
                            inputProps={{
                              "data-testid": "txtf_checklist_answer_number-" + entryIndex,
                              inputMode: "decimal",
                              sx: {
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 16,
                                letterSpacing: "0.5px"
                              }
                            }}
                            InputLabelProps={{
                              ...checklistInputLabelProps
                            }}
                          />
                        ) : null}

                        {entry.answerType === "text" ? (
                          <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            sx={checklistTextFieldSx}
                            label={
                              (entry.answerTitle || "Antworttext") +
                              getOptionalSuffix(entry)
                            }
                            value={entryAnswer.textValue || ""}
                            disabled={readOnly}
                            onChange={event =>
                              updateEntryAnswer(entry.id, {
                                textValue: event.target.value
                              })
                            }
                            inputProps={{
                              "data-testid": "txtf_checklist_answer_text-" + entryIndex,
                              sx: {
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 16,
                                letterSpacing: "0.5px"
                              }
                            }}
                            InputLabelProps={{
                              ...checklistInputLabelProps
                            }}
                          />
                        ) : null}

                        <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                          <Button
                            variant="contained"
                            onClick={() => handleSaveEntryAnswer(entry)}
                            disabled={readOnly || uploadingEntryIds.includes(entry.id)}
                            data-testid={"btn_save_checklist_answer-" + entryIndex}
                            sx={{
                              backgroundColor: "#0E3F51",
                              boxShadow: "none",
                              color: "#FFFFFF",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: 14,
                              fontWeight: 500,
                              height: 36,
                              letterSpacing: "1.25px",
                              lineHeight: 1.2,
                              px: 2,
                              textTransform: "uppercase",
                              "&:hover": {
                                backgroundColor: "#0E3F51",
                                boxShadow: "none"
                              }
                            }}
                          >
                            Antwort speichern
                          </Button>

                          {/* The checklist author's setting "Vergangene
                              Checklisten-Antworten einsehbar machen" decides,
                              also before any answer exists (the dialog then
                              says there are none); without it, no button. */}
                          {entry.historyVisible ? (
                            <Button
                              variant="outlined"
                              onClick={() => setHistoryEntry(entry)}
                              sx={{
                                borderColor: "rgba(0,0,0,0.12)",
                                color: "#0E3F51",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 14,
                                fontWeight: 500,
                                height: 36,
                                letterSpacing: "1.25px",
                                lineHeight: 1.2,
                                px: 2,
                                textTransform: "uppercase",
                                "&:hover": {
                                  borderColor: "rgba(0,0,0,0.24)",
                                  backgroundColor: "transparent"
                                }
                              }}
                            >
                              Vergangene Antworten
                            </Button>
                          ) : null}
                        </Box>

                        {unsavedHintEntryId === entry.id ? (
                          <Typography
                            role="alert"
                            data-testid={"txt_checklist_unsaved_answer-" + entryIndex}
                            sx={{
                              color: "#FF3D00",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: 14,
                              letterSpacing: "0.25px",
                              lineHeight: 1.5
                            }}
                          >
                            Bitte speichern Sie Ihre Antwort, bevor Sie zum nächsten Eintrag wechseln.
                          </Typography>
                        ) : null}

                        {CHECKLIST_ATTACHMENTS_ENABLED && entry.uploadRequired ? (
                          <Box sx={{ pt: 1 }}>
                            <Typography
                              sx={{
                                color: isMissingUpload ? "#FF3D00" : "rgba(0, 0, 0, 0.87)",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: 16,
                                fontWeight: 400,
                                letterSpacing: "0.5px",
                                lineHeight: 1.4,
                                mb: 1.5
                              }}
                            >
                              {(entry.uploadTitle || "Titel des Uploads") +
                                getOptionalSuffix(entry)}
                            </Typography>
                            <ChecklistEntryPhotos
                              uploadNodeId=""
                              resolveUploadNodeId={resolveCurrentReportNodeId}
                              missingNodeIdHint="Die Fotos konnten nicht hochgeladen werden, weil die Checkliste nicht gestartet werden konnte. Bitte versuchen Sie es erneut."
                              photos={entryAnswer.photos || []}
                              disabled={readOnly}
                              invalid={isMissingUpload && !readOnly}
                              onChange={update => updateEntryPhotos(entry.id, update)}
                              onUploadingChange={uploading =>
                                setEntryUploading(entry.id, uploading)
                              }
                            />
                          </Box>
                        ) : null}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={4}
                sx={checklistTextFieldSx}
                label="Ihr Kommentar (optional)"
                value={answerDraft.comment}
                disabled={readOnly}
                onChange={event =>
                  setAnswerDraft(current => ({
                    ...current,
                    comment: event.target.value
                  }))
                }
                inputProps={{
                  sx: {
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 16,
                    letterSpacing: "0.5px"
                  }
                }}
                InputLabelProps={{
                  ...checklistInputLabelProps
                }}
              />

              {/* Answering happens in the Durchführung tab, not when read-only. */}
              {!readOnly ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  pt: 3
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => persistAnswers(true)}
                  disabled={uploadingEntryIds.length > 0}
                  data-testid="btn_complete_checklist"
                  sx={{
                    backgroundColor: "#0E3F51",
                    boxShadow: "none",
                    color: "#FFFFFF",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    height: 36,
                    letterSpacing: "1.25px",
                    lineHeight: 1.2,
                    px: 2,
                    textTransform: "uppercase",
                    "&:hover": {
                      backgroundColor: "#0E3F51",
                      boxShadow: "none"
                    }
                  }}
                >
                  Checkliste abschliessen
                </Button>

                <Button
                  variant="outlined"
                  onClick={() => persistAnswers(false)}
                  disabled={uploadingEntryIds.length > 0}
                  data-testid="btn_pause_checklist"
                  sx={{
                    borderColor: "rgba(0,0,0,0.12)",
                    color: "#0E3F51",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    height: 36,
                    letterSpacing: "1.25px",
                    lineHeight: 1.2,
                    px: 2,
                    textTransform: "uppercase",
                    "&:hover": {
                      borderColor: "rgba(0,0,0,0.24)",
                      backgroundColor: "transparent"
                    }
                  }}
                >
                  Später fortsetzen
                </Button>
              </Box>
              ) : null}
            </Box>
          ) : null}
        </Dialog>

        <Dialog
          open={confirmCloseOpen}
          onClose={() => setConfirmCloseOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "6px",
              boxShadow:
                "0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2)",
              width: 320,
              m: 2,
              overflow: "hidden"
            }
          }}
        >
          <Box sx={{ pb: 1, pl: 3, pr: 2, pt: 3 }}>
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.5)",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 400,
                letterSpacing: "0.25px",
                lineHeight: 1.6
              }}
            >
              Möchten Sie den Dialog wirklich schließen? Nicht gespeicherte
              Änderungen gehen verloren.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
              pb: 1,
              pr: 1,
              pt: 4.5
            }}
          >
            <Button
              onClick={() => setConfirmCloseOpen(false)}
              sx={{
                color: "#38748D",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "1.25px",
                lineHeight: 1.2,
                minWidth: "unset",
                px: 1,
                textTransform: "uppercase"
              }}
            >
              Abbrechen
            </Button>
            <Button
              onClick={resetChecklistDialog}
              sx={{
                color: "#FF3D00",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: "1.25px",
                lineHeight: 1.2,
                minWidth: "unset",
                px: 1,
                textTransform: "uppercase"
              }}
            >
              Schließen
            </Button>
          </Box>
        </Dialog>

        <Snackbar
          open={showValidationError}
          autoHideDuration={4000}
          onClose={(event, reason) => {
            if (reason === "clickaway") return;
            setShowValidationError(false);
          }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              border: "2px solid #FF3D00",
              borderRadius: "4px",
              boxShadow:
                "0px 6px 8px rgba(0, 0, 0, 0.08), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 3px 5px rgba(0, 0, 0, 0.12)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              minHeight: 148,
              p: "14px",
              width: 345
            }}
          >
            <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, width: "100%" }}>
              <ErrorOutlineIcon />
              <Typography
                sx={{
                  color: "#FF3D00",
                  flex: 1,
                  fontFamily: "Bai Jamjuree, sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: "0.15px",
                  lineHeight: 1.4
                }}
              >
                Antworten fehlen
              </Typography>
            </Box>
            <Typography
              sx={{
                color: "rgba(0,0,0,0.87)",
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                fontWeight: 400,
                letterSpacing: "0.25px",
                lineHeight: 1.6
              }}
            >
              Reichen Sie zu den rot markierten Einträgen eine Antwort ein, um die
              Checkliste abschließen zu können. Sie können die Bearbeitung auch später
              fortsetzen.
            </Typography>
          </Box>
        </Snackbar>

        <Snackbar
          open={Boolean(checklistReportFailures?.length)}
          autoHideDuration={6000}
          onClose={(event, reason) => {
            if (reason === "clickaway") return;
            clearChecklistReportFailures();
          }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              border: "2px solid #FF3D00",
              borderRadius: "4px",
              boxShadow:
                "0px 6px 8px rgba(0, 0, 0, 0.08), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 3px 5px rgba(0, 0, 0, 0.12)",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              p: "14px",
              width: 345
            }}
          >
            <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, width: "100%" }}>
              <ErrorOutlineIcon />
              <Typography
                sx={{
                  color: "#FF3D00",
                  flex: 1,
                  fontFamily: "Bai Jamjuree, sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: "0.15px",
                  lineHeight: 1.4
                }}
              >
                Checkliste konnte nicht übermittelt werden
              </Typography>
            </Box>
            {(checklistReportFailures || []).map((failure, index) => (
              <Typography
                key={index}
                sx={{
                  color: "rgba(0,0,0,0.87)",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  letterSpacing: "0.25px",
                  lineHeight: 1.6
                }}
              >
                {failure}
              </Typography>
            ))}
          </Box>
        </Snackbar>

        <ChecklistAnswerHistoryDialog
          open={Boolean(historyEntry)}
          onClose={() => setHistoryEntry(null)}
          checklistNodeId={selectedChecklist?.nodeid}
          entry={historyEntry}
        />
      </>
    );
  }
);
